import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { supabase } from '../lib/supabaseClient';
import { isEmbeddedBrowser, requestEmbeddedBrowserNotice } from '../utils/browser';

const AuthContext = createContext({
    user: null,
    session: null,
    loading: true,
    signInWithGoogle: async () => undefined,
    signOut: async () => undefined,
});

const resolveUser = (session) => {
    if (!session?.user) {
        return null;
    }

    const { user } = session;
    const identities = user.identities || [];
    const provider = user.app_metadata?.provider;
    const identity = identities.find((item) => item.provider === provider) || identities[0] || null;

    return {
        id: user.id,
        name: user.user_metadata?.name || user.user_metadata?.full_name || user.email,
        email: user.email,
        avatar: user.user_metadata?.avatar_url,
        provider: identity?.provider || provider,
        providerId: identity?.identity_data?.sub || identity?.id || null,
        accessToken: session.access_token,
    };
};

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!supabase) {
            setLoading(false);
            return () => undefined;
        }

        let isMounted = true;

        supabase.auth.getSession().then(({ data }) => {
            if (!isMounted) {
                return;
            }

            setSession(data.session);
            setUser(resolveUser(data.session));
            setLoading(false);
        });

        const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            setSession(nextSession);
            setUser(resolveUser(nextSession));
        });

        return () => {
            isMounted = false;
            listener?.subscription.unsubscribe();
        };
    }, []);

    const signInWithGoogle = useCallback(async () => {
        if (!supabase) {
            throw new Error('Supabase is not configured');
        }

        if (isEmbeddedBrowser()) {
            requestEmbeddedBrowserNotice({
                force: true,
                persist: false,
                messages: {
                    description: 'Google sign-in is blocked inside this in-app browser. Open the site in your default browser, then try again.',
                    primaryCta: 'Open in browser',
                    secondaryCta: 'Stay here',
                },
            });
            throw new Error('Open this site in your default browser to continue with Google sign-in.');
        }

        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin,
            },
        });

        if (error) {
            throw error;
        }
    }, []);

    const signOut = useCallback(async () => {
        if (!supabase) {
            return;
        }

        const { error } = await supabase.auth.signOut();
        if (error) {
            throw error;
        }
    }, []);

    const value = useMemo(() => ({
        user,
        session,
        loading,
        signInWithGoogle,
        signOut,
        isConfigured: Boolean(supabase),
    }), [user, session, loading, signInWithGoogle, signOut]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
